import React, {
	Context,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setHiddenTitles } from '../../stores/actions/video';
import { GreenroomState as GS } from '../../stores/types/types';
import { setVideoTitlesHaveFired } from '../../stores/actions/shots';
import { SocketContext } from '../socket';
import { ROOM_STATES } from '../../stores/reducers/settings';

export enum ANIMATION_STATES {
	OFF = 'off', // the animation is initializing
	STARTING = 'starting', // the animation is rendering and moving in
	AUTO_OUT = 'auto-out', // the animation has started to move out automatically
	MANUAL_OUT = 'manual-out', // the animation is moving out manually
	COMPLETE = 'complete', // the animation out is complete
};
export interface Titles {
	hidden: boolean,
	// titles only animate if they are on the canvas or the thumbnail for the current shot
	// this context also only dispatches if they are on the canvas
	isCanvas: boolean, 
	animationState: ANIMATION_STATES
	onAnimationStart: (e: React.AnimationEvent<HTMLDivElement>) => void,
	onAnimationEnd: (e: React.AnimationEvent<HTMLDivElement>) => void,
}

export const baseState = {
	hidden: true,
	isCanvas: false,
	animationState: ANIMATION_STATES.OFF,
	onAnimationStart: () => { },
	onAnimationEnd: () => { },
};

export const TitlesContext: Context<Titles> = React.createContext<Titles>(baseState);

// the shot information passed in is either the canvas shot, or the 
// shot editing modal shot
interface OwnProps {
	children?: React.ReactNode;
	shotId: number; // shot.shot_id
	titlesOn: boolean; // shot.shot_data.videoTitlesOn
	isCanvas?: boolean; // for thumbnails to not dispatch
	isShotModal?: boolean; // for editing modal
}

interface SocketMessage {
	message: [Array<number>, number]
}

export const TitlesProvider = (
	props: OwnProps,
): React.ReactElement<any, React.Provider<Titles>> => {
	const {
		shotId,
		titlesOn,
		isCanvas = false,
		isShotModal = false,
	} = props;

	const dispatch = isCanvas ? useDispatch() : () => { };

	const hiddenTitles = useSelector((s: GS) => s.VideoReducer.hiddenTitles);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const {
		videoTitlesHaveFired,
		shotStartTimestampEntity
	} = useSelector((s: GS) => s.ShotsReducer);

	const [fontsLoaded, setFontsLoaded] = useState<boolean>(true);

	const [
		animationState,
		setAnimationState
	] = useState<ANIMATION_STATES>(ANIMATION_STATES.OFF);

	const {socket} = useContext(SocketContext);

	const currAnimation = useRef<ANIMATION_STATES>(animationState);
	useEffect(() => {
		currAnimation.current = animationState
	}, [animationState]);

	const handleSocketMessage = (
		contextShotId: number,
		{ message }: SocketMessage
	) => {
		const [nHiddenTitles, receivedShotId] = message;

		if (contextShotId === receivedShotId) {
			dispatch(setHiddenTitles(nHiddenTitles));
			dispatch(setVideoTitlesHaveFired(nHiddenTitles[0] === 1 ? true : false));
		};
	};

	useEffect(() => {
		setAnimationState(ANIMATION_STATES.STARTING);

		if (isCanvas) {
			socket?.addListener(
				'toggle-all-titles',
				(m: SocketMessage) => handleSocketMessage(shotId, m)
			);
		}

		return () => {
			socket?.removeListener('toggle-all-titles');
		}
	}, [shotId]);

	useEffect(() => {
		if (document?.fonts) {
			// prevent the component from rendering until web fonts have been loaded
			document.fonts.onloading = () => { setFontsLoaded(false); };
			document.fonts.onloadingdone = () => { setFontsLoaded(true); };
		} else {
			//for tests where the document doesnt exist
			setFontsLoaded(true);
		}
	}, []);

	useEffect(() => {
		if (!videoTitlesHaveFired) {
			setAnimationState(ANIMATION_STATES.STARTING);
		}
	}, [videoTitlesHaveFired]);

	useEffect(() => {
		// When the first item in the hiddenTitles array is 0, we restart title animations
		if (hiddenTitles[0] === 0) {
			setAnimationState(ANIMATION_STATES.STARTING);
			dispatch(setHiddenTitles([]));
		}
	}, [hiddenTitles[0]]);

	useEffect(() => {
		if (titlesOn) {
			setAnimationState(ANIMATION_STATES.STARTING);
		} else {
			setAnimationState(ANIMATION_STATES.OFF);
		}
	}, [titlesOn]);

	const onAnimationStart = (e: React.AnimationEvent<HTMLDivElement>) => {
		if (e.animationName.includes('out')) {
			setAnimationState(ANIMATION_STATES.AUTO_OUT);
		}
	};

	const onAnimationEnd = useCallback((e: React.AnimationEvent<HTMLDivElement>): void => {
		if (
			!isShotModal
			&& e.animationName.includes('out')
			&& currAnimation.current.includes('out')
		) {
			dispatch(setVideoTitlesHaveFired(true));
			dispatch(setHiddenTitles([1]));
			setAnimationState(ANIMATION_STATES.COMPLETE);
		}
	}, []);

	// shot start timestamp doesns't exist when we're in showbuilder
	const showbuilderOrPlaying = roomState === ROOM_STATES.EDITING
		|| shotStartTimestampEntity?.entityValue?.start;

	const hidden = (
		!titlesOn
		|| !fontsLoaded
		|| !showbuilderOrPlaying
		|| animationState === ANIMATION_STATES.OFF
		|| animationState === ANIMATION_STATES.COMPLETE
	)

	return <TitlesContext.Provider value={{
		hidden,
		isCanvas,
		animationState,
		onAnimationStart,
		onAnimationEnd,
	}}>
		{props.children}
	</TitlesContext.Provider>;
};
